import { useMemo, useRef, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { Panel, ReactTable, Select } from '@crazyegginc/hatch';

import { useTimeZonedFunctions } from '/src/hooks';
import { formatCurrency } from '/src/utils';
import { toAtom } from '/src/utils/string';
import { goalPageConversionsQuery } from '../queries';
import { goalSelectedDatesAtom } from '../store';
import { SearchInput } from '/src/components/search-input';
import { PaginationControls } from '/src/components/PaginationControls';
import { SORT_ORDER_TYPES } from '/src/features/_global/constants';
import { PER_PAGE_OPTIONS } from '../constants';

import { ReactComponent as NoResultsIcon } from '@crazyegginc/hatch/dist/images/icon-no-results-outline.svg';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 5;

export function GoalPageConversionsTable({ goal }) {
  const { getConvertedDateRange } = useTimeZonedFunctions();

  const tableRef = useRef(null);
  const [order, setOrder] = useState('totalConversions');
  const [sort, setSort] = useState(SORT_ORDER_TYPES.DESC);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selection] = useAtom(goalSelectedDatesAtom);

  const convertedDateRange = useMemo(
    () =>
      getConvertedDateRange({
        start_date: selection.startDate,
        end_date: selection.endDate,
        special: selection.special,
      }),
    [getConvertedDateRange, selection],
  );

  const { data, isFetching, error, isInitialLoading } = useQuery({
    ...goalPageConversionsQuery({
      id: Number(goal?.id),
      siteId: goal?.siteId,
      startDate: convertedDateRange.startDate,
      endDate: convertedDateRange.endDate,
      search,
      page,
      perPage,
      order: {
        field: toAtom(order),
        sort,
      },
    }),
    enabled: !!goal?.id && !!goal?.siteId,
    keepPreviousData: true,
  });

  const queryData = useMemo(() => data?.goalPageConversions, [data]);

  const pageConversions = useMemo(() => {
    return queryData?.list ?? [];
  }, [queryData]);

  const currency = useMemo(() => pageConversions?.[0]?.totalValue.displayCurrency ?? 'USD', [pageConversions]);

  const metaData = useMemo(() => {
    return queryData?.meta ?? null;
  }, [queryData]);

  const sortData = useMemo(() => {
    return {
      id: order,
      desc: sort === SORT_ORDER_TYPES.DESC,
    };
  }, [order, sort]);

  const columns = useMemo(
    () => [
      {
        header: () => {
          return <div className="pl-2.5">Page URL</div>;
        },
        accessorKey: 'pageUrl',
        size: 115,
        meta: {
          justify: 'left',
          align: 'center',
          padding: '0px 0px 0px 15px',
        },
        cell: ({ row }) => {
          return <span className="mr-4 truncate">{row.original.pageUrl}</span>;
        },
      },
      {
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Total
            <br />
            Conversions
          </span>
        ),
        accessorKey: 'totalConversions',
        size: 25,
        meta: {
          justify: 'right',
          align: 'center',
        },
        cell: ({ row }) => {
          return <span className="mr-4">{row?.original?.totalConversions.toLocaleString() || 0}</span>;
        },
      },
      {
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Visitors
            <br />
            Converting
          </span>
        ),
        accessorKey: 'totalVisitors',
        size: 25,
        meta: {
          justify: 'right',
          align: 'center',
        },
        cell: ({ row }) => {
          return <span className="mr-4">{row?.original?.totalVisitors.toLocaleString() || 0}</span>;
        },
      },
      {
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Total Conversion
            <br />
            Value ({currency})
          </span>
        ),
        accessorKey: 'totalValue',
        size: 26,
        meta: {
          justify: 'right',
          align: 'center',
        },
        cell: ({ row }) => {
          return (
            <span className="mr-4">
              {formatCurrency(row.original.totalValue.displayWorth, currency, { precision: 10 ** 4 })}
            </span>
          );
        },
      },
    ],
    [currency],
  );

  const onFetchData = useCallback(
    ({ sorting = null }) => {
      if (sorting) {
        setOrder(sorting[0].id);
        const nextSort = sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC;
        setSort(nextSort);
        setPage(DEFAULT_PAGE);
      }
    },
    [setOrder, setSort],
  );

  const hasNoResults = !isInitialLoading && !isFetching && pageConversions.length === 0;

  if (error) return null;

  return (
    <>
      <div className="mb-5 mt-10 flex items-center justify-between">
        <h2 className="text-header-3 mr-4">Conversions by page</h2>
        <SearchInput
          placeholder="Search Page URL"
          className="!border-solitude-500 !bg-solitude-500 !font-semibold !text-dodger-blue-500 !placeholder-dodger-blue-500/50"
          search={(value) => {
            if (page !== DEFAULT_PAGE) {
              // reset page to avoid issues with filtering on the offset
              setPage(DEFAULT_PAGE);
            }
            setSearch(value);
          }}
        />
      </div>
      <Panel className="!m-0 !p-0" id="conversions-per-page">
        <div className="relative w-full">
          <ReactTable
            nested={true}
            ref={{ tableRef }}
            useVirtualization={false}
            fetching={isFetching}
            rowPadding={true}
            rowHeight={64}
            headerHeight={70}
            columns={columns}
            data={pageConversions}
            onFetchData={onFetchData}
            enableSorting={true}
            sorting={sortData}
            className="border-none rounded-t"
            headerCustomClassname="!border-solid"
          />
          {hasNoResults ? (
            <div className="w-full h-full top-0 left-0 z-10 bg-white bg-opacity-60 flex justify-center items-end">
              <div className="flex flex-col items-center mb-[65px]">
                <NoResultsIcon className="w-8 h-8 opacity-50 mb-2.5" />
                {search && search !== '' ? (
                  <span className="text-body-2">
                    Zero results for <strong className="text-body-1">&ldquo;{search}&rdquo;</strong>.
                  </span>
                ) : (
                  <span className="text-body-2">Zero results for this date range.</span>
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex h-[61px] w-full items-center justify-end border-t border-mystic-500">
          <div className="flex w-full pl-[35px] pr-[21px] items-center justify-between">
            <div className="text-body-2 flex items-center space-x-1.5">
              <span className="text-lynch-500 text-sm">Show</span>
              <div className="w-30">
                <Select
                  options={PER_PAGE_OPTIONS.map((x) => ({ value: x }))}
                  value={perPage}
                  onChange={(value) => setPerPage(value)}
                />
              </div>
            </div>
            <PaginationControls
              currentPage={page}
              perPage={perPage}
              total={metaData?.total ?? 0}
              setPage={(page) => setPage(page)}
              forceShow={true}
            />
          </div>
        </div>
      </Panel>
    </>
  );
}
